/**
 * ImprovementConversationProvider — the app-wide opener for the improvement conversation panel.
 *
 * `openConversation` is the single opener (IMP-BR-6). It is called with a `start` only from the
 * closed entry set: the message entry, the held-draft entry, the case-header entry, the queue's
 * "Tell the agent why" and the playbook page's "Talk to the agent"; and without a `start`
 * (reopening a known conversation) from the reports list.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { PlaybookAgentKind } from '@resida/api-types/agent';
import type { ImprovementErrorKind } from '../../../api/improvement-conversations.api';
import { ImprovementConversationSheet } from './ImprovementConversationSheet';
import type { ConversationStart } from './useImprovementConversation';

/** What the panel shows about the thing the conversation is about, before any turn. */
export interface ConversationAnchorCard {
  /** Small caps label, e.g. "The agent sent". */
  label: string;
  text: string;
  /** Already formatted for the property's timezone. */
  at?: string;
}

export interface OpenConversationRequest {
  /** Reopen this conversation (reports list); mutually exclusive with `start`. */
  conversationId?: string;
  /** Start a new conversation on the first landlord message. */
  start?: ConversationStart;
  agentKind?: PlaybookAgentKind;
  /** Header subtitle, e.g. "About a message on 12 Elm St, Unit 2". */
  subtitle?: string;
  anchorCard?: ConversationAnchorCard;
  /** The anchored case's property, for the scope switch on a proposal. */
  property?: { id: string; name: string };
  /** The first message did not start a conversation (rate limited, runtime unavailable). */
  onStartFailed?: (kind: ImprovementErrorKind) => void;
}

interface ImprovementConversationContextValue {
  openConversation: (request: OpenConversationRequest) => void;
}

const ImprovementConversationContext = createContext<ImprovementConversationContextValue | null>(null);

export function ImprovementConversationProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<(OpenConversationRequest & { key: number }) | null>(null);
  // The control that opened the panel; focus returns to it when the panel closes.
  const openerRef = useRef<HTMLElement | null>(null);
  const openConversation = useCallback((next: OpenConversationRequest) => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    setRequest({ ...next, key: Date.now() });
  }, []);
  const value = useMemo(() => ({ openConversation }), [openConversation]);

  // Following a link out of the panel ("See it in your rules", "Edit my rules") leaves the page the
  // panel was opened over, so the panel closes with it.
  const { pathname } = useLocation();
  useEffect(() => {
    setRequest(null);
  }, [pathname]);

  return (
    <ImprovementConversationContext.Provider value={value}>
      {children}
      {request && <ImprovementConversationSheet key={request.key} request={request} returnFocusTo={openerRef.current} onClose={() => setRequest(null)} />}
    </ImprovementConversationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useImprovementConversationOpener(): ImprovementConversationContextValue['openConversation'] {
  const ctx = useContext(ImprovementConversationContext);
  if (!ctx) throw new Error('useImprovementConversationOpener must be used inside ImprovementConversationProvider');
  return ctx.openConversation;
}
